import React, { useEffect, useState, useMemo } from "react";
import './GraficasIncidencias.scss';
import Card from "../../../../../components/Atomsv2/Card/Card";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { traerSemana, traerRangoPorSemana } from "../../AdministracionSemaforoIntegracion";
import Skeleton from "../../../../../components/Atomsv2/Skeleton/Skeleton";
import Table from "../../../../../components/moleculesv2/Table/Table";
import Column from "../../../../../components/moleculesv2/Table/Column";



// ─── Helpers ──────────────────────────────────────────────────────────────────

// Agrupa registros por semana y construye el conteo de clientes por semana
const agruparPorSemana = (items = []) => {
    const mapa = {};
    items.forEach(({ SEMANA, CLIENTE, INICIO_SEMANA, FIN_SEMANA }) => {
        if (!mapa[SEMANA]) mapa[SEMANA] = { SEMANA: `S${SEMANA}`, total: 0, clientes: {}, fechaInicio: INICIO_SEMANA ?? '', fechaFin: FIN_SEMANA ?? '' };
        mapa[SEMANA].total += 1;
        const nombre = (CLIENTE || '').trim();
        mapa[SEMANA].clientes[nombre] = (mapa[SEMANA].clientes[nombre] || 0) + 1;
    });
    return Object.values(mapa).sort((a, b) =>
        parseInt(a.SEMANA.slice(1)) - parseInt(b.SEMANA.slice(1))
    );
};

// ─── Tooltip personalizado ────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const { clientes = {}, fechaInicio, fechaFin } = payload[0]?.payload || {};
    return (
        <div className="tooltip-graficaIncidencias">
            <p className="tooltip-titulo">Semana {label}</p>
            {fechaInicio && <p className="tooltip-fecha">Inicio: <strong>{fechaInicio}</strong></p>}
            {fechaFin && <p className="tooltip-fecha">Fin: <strong>{fechaFin}</strong></p>}
            <p className="tooltip-total">Total: <strong>{payload[0].value}</strong></p>
            <hr />
            {Object.entries(clientes).map(([cliente, cantidad]) => (
                <p key={cliente} className="tooltip-cliente"><span>{cliente}:</span> {cantidad}</p>
            ))}
        </div>
    );
};

// ─── Tarjeta de gráfica ───────────────────────────────────────────────────────
const GraficaCard = ({ titulo, datos }) => (
    <div className="graficaIncidencias">
        <Card>
            <p className="titulo-graficaIncidencias">{titulo}</p>
            <ResponsiveContainer width="100%" height={220}>
                <BarChart data={datos} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="SEMANA" />
                    <YAxis allowDecimals={false} width={30} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="total" fill="#e76e1e" barSize={20} radius={[4, 4, 0, 0]} />
                </BarChart>
            </ResponsiveContainer>
        </Card>
    </div>
);

// ─── Componente principal ─────────────────────────────────────────────────────
const GraficasIncidencias = ({ data, loading, filtroZona, categoria = 'TODOS', periodo = '4semanas', semanaCustom = null }) => {
    const [dataFiltrada, setDataFiltrada] = useState([]);

    useEffect(() => {
        if (!data) return setDataFiltrada([]);

        const { semana: semanaActual } = traerSemana();
        const anioActual = new Date().getFullYear();
        const semanaBase = semanaCustom ?? semanaActual;

        const resultado = data.filter((item) => {
            const mismoAnio = Number(item.AÑO) === anioActual;
            const mismaZona = !filtroZona || item.ZONA === filtroZona;
            const mismaCategoria =
                categoria === 'SUMA' ? item.INCIDENTE_SUMA === 1
                    : categoria === 'ALIADOS' ? item.INCIDENTE_ALIADO === 1
                        : true;
            if (periodo === '4semanas') {
                const rango = traerRangoPorSemana(semanaBase, 3);
                return mismoAnio && mismaZona && mismaCategoria &&
                    Number(item.SEMANA) >= rango.semanaInicio && Number(item.SEMANA) <= rango.semanaFin;
            }
            if (periodo === 'hoy') {
                const rango = traerRangoPorSemana(semanaBase, 0);
                return mismoAnio && mismaZona && mismaCategoria &&
                    Number(item.SEMANA) >= rango.semanaInicio && Number(item.SEMANA) <= rango.semanaFin;
            }
            return mismoAnio && mismaZona && mismaCategoria; // 'todo'
        });

        setDataFiltrada(resultado);
    }, [data, periodo, filtroZona, categoria, semanaCustom]);

    // Causas únicas presentes en la data filtrada — se generan dinámicamente del API
    const causasUnicas = useMemo(() => {
        const mapa = {};
        dataFiltrada.forEach(({ ID_CAUSA, CAUSA }) => {
            if (ID_CAUSA != null && !mapa[ID_CAUSA]) {
                mapa[ID_CAUSA] = (CAUSA || '').trim();
            }
        });
        return Object.entries(mapa)
            .map(([id, titulo]) => ({ id_causa: Number(id), titulo }))
            .sort((a, b) => a.titulo.localeCompare(b.titulo));
    }, [dataFiltrada]);

    return (
        <div className="GraficasIncidencias">
            {loading ? (
                <>
                    <Skeleton type="rect" height="40px" width="200px" style={{ marginBottom: 16 }} />
                    <div className="container-graficasIncidencias">
                        <Skeleton type="rect" height="260px" />
                        <Skeleton type="rect" height="260px" />
                        <Skeleton type="rect" height="260px" />
                        <Skeleton type="rect" height="260px" />
                    </div>
                </>
            ) : (
                <>
                    <div className="container-graficasIncidencias">
                        {causasUnicas.map(({ titulo, id_causa }) => (
                            <GraficaCard
                                key={id_causa}
                                titulo={titulo}
                                datos={agruparPorSemana(dataFiltrada.filter(item => Number(item.ID_CAUSA) === id_causa))}
                            />
                        ))}
                    </div>
                    <div style={{ marginTop: "10px" }}></div>
                </>
            )}
            <Table responsiveHideColumn showCounter skeleton={loading} download globalFilter data={dataFiltrada} height={'calc(100vh - 80px)'} style={{ width: '100%' }}>
                <Column responsive sortable header='FOLIO' value={"ID_INCIDENTE"}></Column>
                <Column responsive sortable header='FECHA INICIO' value={"INICIO_SEMANA"}></Column>
                <Column responsive sortable header='FECHA FIN' value={"FIN_SEMANA"}></Column>
                <Column responsive sortable header='ZONA' value={"ZONA"}></Column>
                <Column responsive sortable header='CLIENTE' value={"CLIENTE"}></Column>
                <Column responsive sortable header='RUTA' value={"RUTA"}></Column>
                <Column responsive sortable header='CAUSA' value={"CAUSA"}></Column>
                <Column responsive sortable header='EFECTO' value={"EFECTO"}></Column>
                <Column responsive sortable header='DETALLE' value={"DETALLE"}></Column>
                <Column responsive sortable header='TIPO_INCIDENTE' value={"TIPO_INCIDENTE"}></Column>
                <Column responsive sortable header='CAPTURISTA' value={"CAPTURISTA"}></Column>
            </Table>
        </div>
    );
};

export default GraficasIncidencias;